import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, GraduationCap, Search, Lightbulb, CheckCircle2, XCircle, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function QuizCard({ item, kind, index, onCorrect }) {
  const [picked, setPicked] = useState(null);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const promptText = kind === "trivia" ? item.question : item.prompt;

  const choose = async (i) => {
    if (res) return;
    setPicked(i);
    setLoading(true);
    try {
      const path = kind === "trivia" ? "/kids/trivia/check" : "/kids/spot/check";
      const { data } = await api.post(path, { id: item.id, choice_index: i });
      setRes(data);
      if (data.correct) onCorrect(item.id);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      data-testid={`kids-question-${item.id}`}
      initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06 }}
      className="rounded-2xl p-6 sm:p-7"
      style={{ background: "#FFFDF6", border: "2px solid #EBD9AE", boxShadow: "0 12px 30px rgba(139,90,43,0.15)" }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="flex items-center justify-center w-7 h-7 rounded-full font-typewriter text-xs" style={{ background: "#F59E0B", color: "#3A2A15" }}>{index + 1}</span>
        <span className="font-typewriter text-[10px] uppercase tracking-[0.25em]" style={{ color: "#B98A4B" }}>
          {kind === "trivia" ? "Detective Trivia" : "Spot the Brain Rot"}
        </span>
      </div>
      <p className="font-serif-hq text-xl sm:text-2xl leading-snug mb-5" style={{ color: "#3A2A15" }}>{promptText}</p>

      <div className="grid gap-3">
        {item.options.map((opt, i) => {
          const isAnswer = res && i === res.answer_index;
          const isWrongPick = res && i === picked && !res.correct;
          let bg = "#FFF7E6", border = "#EBD9AE", color = "#3A2A15";
          if (isAnswer) { bg = "#DCFCE7"; border = "#4ade80"; }
          if (isWrongPick) { bg = "#FEE2E2"; border = "#f87171"; }
          return (
            <button key={i} data-testid={`kids-option-${item.id}-${i}`} disabled={!!res || loading}
              onClick={() => choose(i)}
              className="text-left rounded-xl px-4 py-3 text-sm sm:text-base font-medium flex items-center justify-between gap-3 transition-colors disabled:cursor-default"
              style={{ background: bg, border: `2px solid ${border}`, color }}>
              <span>{opt}</span>
              {isAnswer && <CheckCircle2 size={18} className="text-green-600 shrink-0" />}
              {isWrongPick && <XCircle size={18} className="text-red-500 shrink-0" />}
            </button>
          );
        })}
      </div>

      {res && (
        <motion.div data-testid={`kids-explain-${item.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="mt-4 rounded-xl p-4 flex gap-3" style={{ background: "#FFF7E6", border: "1px dashed #E0B96B" }}>
          <Lightbulb size={18} className="shrink-0 mt-0.5" style={{ color: "#D97706" }} />
          <div>
            <p className="font-typewriter text-[10px] uppercase tracking-widest mb-1" style={{ color: res.correct ? "#16a34a" : "#b45309" }}>
              {res.correct ? "Case cracked!" : "Good try, detective!"}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: "#4A3B28" }}>{res.explain}</p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function KidsAcademy() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("trivia");
  const [trivia, setTrivia] = useState([]);
  const [spot, setSpot] = useState([]);
  const [solved, setSolved] = useState({ trivia: new Set(), spot: new Set() });

  useEffect(() => {
    document.title = "Zombie Detectives Academy";
    api.get("/kids/trivia").then((r) => setTrivia(r.data));
    api.get("/kids/spot").then((r) => setSpot(r.data));
  }, []);

  const items = tab === "trivia" ? trivia : spot;
  const solvedCount = solved[tab].size;
  const total = items.length;

  const markSolved = (id) => setSolved((s) => {
    const next = new Set(s[tab]); next.add(id);
    return { ...s, [tab]: next };
  });

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button data-testid={`kids-tab-${id}`} onClick={() => setTab(id)}
      className="flex items-center gap-2 rounded-full px-5 py-2.5 font-typewriter text-xs uppercase tracking-widest transition-colors"
      style={{ background: tab === id ? "#F59E0B" : "#FFFDF6", color: tab === id ? "#3A2A15" : "#8B5A2B", border: "2px solid #EBD9AE" }}>
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#FBF1D9 0%,#F6E7C4 100%)" }} data-testid="kids-academy-page">
      <div className="mx-auto max-w-4xl px-5 py-10">
        <button data-testid="kids-back-btn" onClick={() => navigate("/")}
          className="flex items-center gap-2 font-typewriter text-xs uppercase tracking-widest mb-8 transition-colors" style={{ color: "#8B5A2B" }}>
          <ArrowLeft size={15} /> Back to Headquarters
        </button>

        <div className="flex items-center gap-3 mb-2">
          <span className="flex items-center justify-center w-12 h-12 rounded-2xl" style={{ background: "#F59E0B" }}>
            <GraduationCap size={26} style={{ color: "#3A2A15" }} />
          </span>
          <p className="font-typewriter text-[11px] uppercase tracking-[0.3em]" style={{ color: "#B98A4B" }}>Junior Investigator Training</p>
        </div>
        <h1 className="font-serif-hq text-4xl sm:text-5xl tracking-tight mb-3" style={{ color: "#3A2A15" }}>Zombie Detectives Academy</h1>
        <p className="max-w-2xl leading-relaxed mb-8" style={{ color: "#6B5636" }}>
          Some videos, games and ads try to sneak into your brain and take over. Your mission, detective:
          learn their tricks and outsmart them! Play the games below — no account needed.
        </p>

        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div className="flex gap-3">
            <TabBtn id="trivia" icon={Search} label="Detective Trivia" />
            <TabBtn id="spot" icon={Lightbulb} label="Spot the Brain Rot" />
          </div>
          <div data-testid="kids-score" className="flex items-center gap-2 rounded-full px-4 py-2" style={{ background: "#FFFDF6", border: "2px solid #EBD9AE" }}>
            <Star size={16} style={{ color: "#F59E0B" }} />
            <span className="font-typewriter text-xs uppercase tracking-widest" style={{ color: "#8B5A2B" }}>{solvedCount} / {total} cracked</span>
          </div>
        </div>

        {solvedCount === total && total > 0 && (
          <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="rounded-2xl p-6 mb-8 flex items-center gap-4" style={{ background: "#DCFCE7", border: "2px solid #4ade80" }}>
            <Star size={28} className="text-amber-500" />
            <div>
              <p className="font-serif-hq text-2xl" style={{ color: "#166534" }}>You cracked every case!</p>
              <p className="text-sm" style={{ color: "#3f6212" }}>
                {user ? "A Junior Detective badge is waiting in your case file." : "Ask a grown-up to help you sign in to collect a Junior Detective badge!"}
              </p>
            </div>
          </motion.div>
        )}

        <div className="grid gap-5">
          {items.map((item, i) => (
            <QuizCard key={item.id} item={item} kind={tab} index={i} onCorrect={markSolved} />
          ))}
        </div>
      </div>
    </div>
  );
}
